import * as React from 'react';
import { BaseHero, Text } from '@blueprint/ui';
import ErrorImage from './ErrorImage';
import { logError } from '@zoopla/logger';

type Props = {
    children: React.ReactNode;
    renderOnError?: React.ReactNode | null;
};

type State = {
    hasError: boolean;
};

class ErrorBoundary extends React.Component<Props, State> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        logError(error, errorInfo);
        if (typeof window !== 'undefined') {
            window.newrelic?.noticeError(error, {
                componentStack: errorInfo.componentStack
            });
        }
    }

    render() {
        const hasDefinedOrNullRenderOnError =
            this.props.renderOnError || this.props.renderOnError === null;

        if (this.state.hasError) {
            return hasDefinedOrNullRenderOnError ? (
                this.props.renderOnError
            ) : (
                <BaseHero
                    title="Something went wrong"
                    image={ErrorImage}
                    content={
                        <Text>
                            Our team has been made aware and are working to find
                            a solution to this problem. Please refresh your page
                            or, if the issue continues, please try again later.
                        </Text>
                    }
                />
            );
        }
        return this.props.children;
    }
}
export default ErrorBoundary;

export function withErrorBoundary<P>(
    WrappedComponent: React.ComponentType<P>,
    renderOnError: React.ReactNode
): React.ComponentType<P> {
    return (props: P) => (
        <ErrorBoundary renderOnError={renderOnError}>
            <WrappedComponent {...props} />
        </ErrorBoundary>
    );
}
